import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Spinner, Alert, Card, Form, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';
import { hrLeadershipTMAPI, hrLearningDevelopmentAPI, hrDigitalEnablementAPI, departmentAPI } from '../utils/api';
import { INIT_PARAMETERS as LEADERSHIP_TM_CONFIG } from './HRLeadershipTMDashboardPage';
import { INIT_PARAMETERS as LEARNING_DEV_CONFIG } from './HRLearningDevelopmentDashboardPage';
import { INIT_PARAMETERS as DIGITAL_ENABLEMENT_CONFIG } from './HRDigitalEnablementDashboardPage';
import { isAdmin } from '../utils/auth';

// Hardcoded INIT_PARAMS from sub-dashboards
const LEADERSHIP_TM_PARAMS = [
  'talent-review-council', 'act-up-star-identification', 'attrition', 'hi-pos-stars-leaders', 
  'stars-movements', 'job-rotation', 'succession-planning'
];

const LEARNING_DEV_PARAMS = [
  'training-business-linkage', 'actual-trainings', 'benchmarking-visits', 'training-roi-mis', 
  'coaching-program', 'induction-familization', 'training-stars-managers', 'skill-matrix', 
  'anchoring-mentoring-idp'
];

const SUB_DASHBOARDS = [
  {
    id: 'leadership-tm',
    title: 'Leadership & Talent Management',
    icon: '👥',
    params: LEADERSHIP_TM_PARAMS,
    api: hrLeadershipTMAPI,
    projectId: 'hr-leadership-tm',
    projectName: 'Leadership & TM',
  },
  {
    id: 'learning-development',
    title: 'Learning & Development',
    icon: '📚',
    params: LEARNING_DEV_PARAMS,
    api: hrLearningDevelopmentAPI,
    projectId: 'hr-learning-development',
    projectName: 'Learning & Development',
  },
  {
    id: 'digital-enablement',
    title: 'Digital & Enablement',
    icon: '💻',
    params: [
      'organization-management',
      'recruiting-onboarding',
      'performance-management-system',
      'talent-management-process',
      'learning-management',
      'reward-recognition',
      'time-leave-management',
      'employee-manager-self-service',
      'e-separation',
      'employee-one-page-profile',
      'digital-dashboards',
    ],
    api: hrDigitalEnablementAPI,
    projectId: 'hr-digital-enablement',
    projectName: 'Digital & Enablement',
  },
];

const computeOverallMetrics = (allRecords) => {
  if (!allRecords || allRecords.length === 0) {
    return { overallPct: null, responsibles: [] };
  }

  // Unique responsibles (top 4)
  const responsibles = [...new Set(allRecords.map(r => r.responsibility).filter(Boolean))]
    .slice(0, 4);

  // Avg completion %
  const validPcts = allRecords
    .filter(r => r.completion_pct != null && !isNaN(Number(r.completion_pct)))
    .map(r => Number(r.completion_pct));
  const overallPct = validPcts.length > 0 
    ? Math.round(validPcts.reduce((a, b) => a + b, 0) / validPcts.length * 10) / 10 
    : null;

  return { overallPct, responsibles };
};

const getFiscalYearStartYear = (date) => {
  if (!date) return null;
  // FY runs Oct 1 -> Sep 30, so FY "start year" is current year if month >= Oct else previous year.
  const month = date.getMonth(); // 0-11
  const year = date.getFullYear();
  return month >= 9 ? year : year - 1;
};

const formatYearLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(-2)}`;


const extractMarksMap = (parameterTree) => {
  const marksBySubParam = {};
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.id && node.marks != null && !Number.isNaN(Number(node.marks))) {
      marksBySubParam[String(node.id)] = Number(node.marks);
    }
    const children = [];
    if (Array.isArray(node.subParameters)) children.push(...node.subParameters);
    if (Array.isArray(node.subSubParameters)) children.push(...node.subSubParameters);
    children.forEach(walk);
  };
  (Array.isArray(parameterTree) ? parameterTree : []).forEach(walk);
  return marksBySubParam;
};

const buildUserOptions = (records) => {
  const usersByKey = new Map();
  (records || []).forEach((r) => {
    const raw = String(r?.responsibility || '').trim();
    if (!raw) return;
    raw
      .split('/')
      .map((s) => s.trim().replaceAll(/\s+/g, ' '))
      .filter(Boolean)
      .forEach((u) => {
        const key = u.toLowerCase();
        if (!usersByKey.has(key)) usersByKey.set(key, u);
      });
  });
  return Array.from(usersByKey.values()).sort((a, b) => a.localeCompare(b));
};

const filterRecordsByUser = (records, selectedUser) => {
  const user = String(selectedUser || '').trim().replaceAll(/\s+/g, ' ');
  const userKey = user ? user.toLowerCase() : '';
  if (!userKey) return records || [];
  return (records || []).filter((r) => {
    const resp = String(r?.responsibility || '');
    return resp
      .split('/')
      .map((s) => s.trim().replaceAll(/\s+/g, ' ').toLowerCase())
      .includes(userKey);
  });
};

const getRecordUpdatedAt = (record) => {
  const candidate = record?.updated_at || record?.created_at;
  const parsed = candidate ? new Date(candidate) : new Date(0);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const buildLatestBySubParam = (records) => {
  const bySubParam = new Map();
  for (const record of (records || [])) {
    const id = String(record?.sub_parameter_id || '');
    if (!id) continue;
    const d = getRecordUpdatedAt(record);
    const prev = bySubParam.get(id);
    if (!prev || d > prev._d) bySubParam.set(id, { ...record, _d: d });
  }
  return bySubParam;
};

const normalizeCompletionPct = (pct) => {
  if (pct === null || pct === undefined) return 0;
  const raw = Number(pct);
  if (Number.isNaN(raw)) return 0;
  return Math.max(0, Math.min(100, raw));
};

const splitResponsibles = (value) => String(value || '')
  .split('/')
  .map((s) => s.trim().replaceAll(/\s+/g, ' '))
  .filter(Boolean);

const computeAchievedMarks = ({ marksMap = {}, latestBySubParam }) => {
  let achieved = 0;
  for (const [subParamId, marks] of Object.entries(marksMap || {})) {
    const record = latestBySubParam?.get(String(subParamId));
    const pct = normalizeCompletionPct(record?.completion_pct);
    achieved += (Number(marks) || 0) * pct / 100;
  }
  return achieved;
};

const accumulateUserWiseMarks = ({ rawRecords = [], marksMap = {}, totalsByUser, displayByKey }) => {
  const latestBySubParam = buildLatestBySubParam(rawRecords);
  for (const [subParamId, marks] of Object.entries(marksMap || {})) {
    const record = latestBySubParam.get(String(subParamId));
    if (!record) continue;
    const pct = normalizeCompletionPct(record?.completion_pct);
    const responsibles = splitResponsibles(record?.responsibility);
    if (!responsibles.length) continue;

    const split = responsibles.length;
    const totalShare = (Number(marks) || 0) / split;
    const achievedShare = (Number(marks) || 0) * pct / 100 / split;

    for (const user of responsibles) {
      const key = user.toLowerCase();
      if (!displayByKey.has(key)) displayByKey.set(key, user);
      const prev = totalsByUser.get(key) || { user: displayByKey.get(key) || user, total: 0, achieved: 0 };
      totalsByUser.set(key, {
        user: displayByKey.get(key) || user,
        total: prev.total + totalShare,
        achieved: prev.achieved + achievedShare,
      });
    }
  }
};

// removed unused bar label props to silence lint warnings
const barValueLabelTopProps = { fill: '#111827', fontSize: 11, fontWeight: 700 };
const pctLabelFormatter = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return `${Math.round(n)}%`;
};

const renderVerticalNameLabel = (props) => {
  const { x, y, width, height, value } = props || {};
  if (x == null || y == null || width == null || height == null || !value) return null;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const label = String(value);
  return (
    <text
      x={cx}
      y={cy}
      fill="#111827"
      fontSize="11"
      textAnchor="middle"
      dominantBaseline="middle"
      transform={`rotate(-90, ${cx}, ${cy})`}
    >
      {label}
    </text>
  );
};

const HRMainDashboardPage = ({ departmentId = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hrDept, setHrDept] = useState(null);
  const [subRecords, setSubRecords] = useState({});
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedFyStartYear, setSelectedFyStartYear] = useState(() => getFiscalYearStartYear(new Date()));
  const [rollingOver, setRollingOver] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('hrSelectedYear', String(selectedFyStartYear));
    } catch {}
  }, [selectedFyStartYear]);

  const handleRollover = async () => {
    if (!isAdmin() || !hrDept?.id) return;
    const fromYear = selectedFyStartYear;
    const toYear = fromYear + 1;
    setRollingOver(true);
    setError('');
    try {
      await Promise.all([
        hrLeadershipTMAPI.rolloverToNextYear({ department_id: hrDept.id, from_year_start: fromYear, to_year_start: toYear }),
        hrLearningDevelopmentAPI.rolloverToNextYear({ department_id: hrDept.id, from_year_start: fromYear, to_year_start: toYear }),
        hrDigitalEnablementAPI.rolloverToNextYear({ department_id: hrDept.id, from_year_start: fromYear, to_year_start: toYear }),
      ]);
      setSelectedFyStartYear(toYear);
      setRefreshKey((v) => v + 1);
    } catch (e) {
      setError(e?.message || 'Failed to rollover to next year.');
    } finally {
      setRollingOver(false);
    }
  };

  // Fetch HR dept if not provided
  useEffect(() => {
    const fetchHrDept = async () => {
      if (departmentId) {
        setHrDept({ id: departmentId });
        return;
      }
      try {
        const response = await departmentAPI.getAllDepartments();
        const depts = response?.data || [];
        const hrDeptData = depts.find(d => 
          d.name && d.name.toLowerCase().includes('hr')
        );
        if (hrDeptData) setHrDept(hrDeptData);
      } catch {}
    };
    fetchHrDept();
  }, [departmentId]);

  useEffect(() => {
    const handleHrDataChanged = () => setRefreshKey((value) => value + 1);
    globalThis.addEventListener('hrDataChanged', handleHrDataChanged);
    return () => globalThis.removeEventListener('hrDataChanged', handleHrDataChanged);
  }, []);

  // Load all records needed for a sub-dashboard (single request per sub-dashboard)
  const loadSubDashboardRecords = useCallback(async (subDashboard, deptId, fyStartYear) => {
    try {
      const response = await subDashboard.api.getAllRecords(deptId, fyStartYear);
      const rows = Array.isArray(response?.data) ? response.data : [];
      return rows;
    } catch {
      return [];
    }
  }, []);

  // Load raw records for all sub dashboards
  useEffect(() => {
    const loadAll = async () => {
      if (!hrDept?.id) return;
      setLoading(true);
      setError('');

      try {
        const recordPromises = SUB_DASHBOARDS.map(async (sub) => ([
          sub.id,
          await loadSubDashboardRecords(sub, hrDept.id, selectedFyStartYear),
        ]));
        const loaded = await Promise.all(recordPromises);
        setSubRecords(Object.fromEntries(loaded));
      } catch (e) {
        setError('Failed to load HR metrics.');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [hrDept, loadSubDashboardRecords, refreshKey, selectedFyStartYear]);

  const configBySubId = useMemo(() => ({
    'leadership-tm': LEADERSHIP_TM_CONFIG,
    'learning-development': LEARNING_DEV_CONFIG,
    'digital-enablement': DIGITAL_ENABLEMENT_CONFIG,
  }), []);

  const marksBySubId = useMemo(() => {
    const entries = Object.entries(configBySubId).map(([subId, config]) => ([
      subId,
      extractMarksMap(config),
    ]));
    return Object.fromEntries(entries);
  }, [configBySubId]);

  const allRecordsFlat = useMemo(() => Object.values(subRecords).flat(), [subRecords]);

  const userOptions = useMemo(() => buildUserOptions(allRecordsFlat), [allRecordsFlat]);

  const fyOptions = useMemo(() => {
    const current = getFiscalYearStartYear(new Date());
    const candidates = [current - 1, current, current + 1].filter((v) => Number.isInteger(v));
    return Array.from(new Set(candidates)).sort((a, b) => b - a);
  }, []);

  useEffect(() => {
    if (fyOptions.length && selectedFyStartYear == null) setSelectedFyStartYear(fyOptions[0]);
  }, [fyOptions, selectedFyStartYear]);

  const derivedSubMetrics = useMemo(() => {
    const entries = SUB_DASHBOARDS.map((sub) => {
      const raw = subRecords[sub.id] || [];
      const filtered = filterRecordsByUser(raw, selectedUser);
      const overall = computeOverallMetrics(filtered);

      const marksMap = marksBySubId[sub.id] || {};
      const totalMarks = Object.values(marksMap).reduce((s, v) => s + (Number(v) || 0), 0);
      const latestBySubParam = buildLatestBySubParam(filtered);
      const achievedMarksRaw = computeAchievedMarks({ marksMap, latestBySubParam });
      const achievedMarks = Math.round(achievedMarksRaw * 10) / 10;
      const marksPct = totalMarks > 0 ? Math.round((achievedMarksRaw / totalMarks) * 1000) / 10 : null;

      return [sub.id, {
        ...overall,
        totalMarks,
        achievedMarks,
        marksPct
      }];
    });

    return Object.fromEntries(entries);
  }, [subRecords, marksBySubId, selectedUser]);

  const userWiseChartData = useMemo(() => {
    const totalsByUser = new Map(); // key -> { user, total, achieved }
    const displayByKey = new Map(); // key -> display

    for (const sub of SUB_DASHBOARDS) {
      const raw = subRecords[sub.id] || [];
      const marksMap = marksBySubId[sub.id] || {};
      accumulateUserWiseMarks({ rawRecords: raw, marksMap, totalsByUser, displayByKey });
    }

    return Array.from(totalsByUser.values())
      .map((row) => ({
        user: row.user,
        achieved: Math.round(row.achieved * 10) / 10,
        total: Math.round(row.total * 10) / 10,
        remaining: Math.round(Math.max(0, row.total - row.achieved) * 10) / 10,
        pct: row.total > 0 ? Math.round((row.achieved / row.total) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.pct - a.pct);
  }, [marksBySubId, subRecords]);

  const handleSubClick = (project) => {
    // Set selected project in localStorage
    localStorage.setItem('selectedProject', JSON.stringify({
      id: project.projectId,
      name: project.projectName,
      departmentId: hrDept.id
    }));
    // Trigger change event
    globalThis.dispatchEvent(new Event('projectChanged'));
    // Navigate to dashboard (reloads DepartmentDashboardsPage with new project)
    navigate('/dashboard', { replace: true });
  };

  const getPctColor = (pct) => {
    if (pct === null) return 'var(--bs-secondary)';
    return pct >= 75 ? 'var(--bs-success)' : pct >= 40 ? 'var(--bs-warning)' : 'var(--bs-danger)';
  };

  if (loading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">Loading HR Dashboard...</p>
      </Container>
    );
  }

  const availableSubPercentages = Object.values(derivedSubMetrics)
    .map((metrics) => metrics?.overallPct)
    .filter((pct) => pct != null && !Number.isNaN(Number(pct)));

  const overallHRPct = availableSubPercentages.length
    ? Math.round(
      (availableSubPercentages.reduce((sum, pct) => sum + Number(pct), 0) / availableSubPercentages.length) * 10
    ) / 10
    : null;

  const overallMarks = Object.values(derivedSubMetrics).reduce((acc, m) => ({
    total: acc.total + (m?.totalMarks || 0),
    achieved: acc.achieved + (m?.achievedMarks || 0),
  }), { total: 0, achieved: 0 });

  const overallMarksPct = overallMarks.total > 0
    ? Math.round((overallMarks.achieved / overallMarks.total) * 1000) / 10
    : null;

  const chartData = SUB_DASHBOARDS.map((sub) => {
    const metrics = derivedSubMetrics[sub.id] || {};
    const total = metrics.totalMarks || 0;
    const achieved = metrics.achievedMarks || 0;
    const pctNum = Number(metrics.marksPct);
    return {
      name: sub.projectName,
      achieved,
      remaining: Math.max(0, total - achieved),
      total,
      pct: Number.isFinite(pctNum) ? pctNum : 0,
    };
  });

  return (
    <Container fluid className="dashboard-page py-4">
      <style>{`
        .hr-main-tile { cursor: pointer; transition: all 0.3s; }
        .hr-main-tile:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.15); }
        .progress-bar-fill { transition: width 0.8s ease; }
      `}</style>

      {error && <Alert variant="warning" className="mb-4">{error}</Alert>}

      <Card className="shadow-sm mb-4 border-0">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label className="text-muted small mb-1">Year (Oct–Sep)</Form.Label>
              <Form.Select
                value={selectedFyStartYear ?? ''}
                onChange={(e) => setSelectedFyStartYear(Number(e.target.value))}
              >
                {fyOptions.map((y) => (
                  <option key={y} value={y}>{formatYearLabel(y)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label className="text-muted small mb-1">User / Responsible</Form.Label>
              <Form.Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                <option value="">All</option>
                {userOptions.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => { setSelectedUser(''); setSelectedFyStartYear(getFiscalYearStartYear(new Date())); }}
              >
                Reset
              </Button>
            </Col>
          </Row>
          {isAdmin() && (
            <div className="mt-3 d-flex justify-content-end">
              <Button variant="primary" onClick={handleRollover} disabled={rollingOver || selectedFyStartYear == null}>
                {rollingOver ? 'Creating next year…' : 'Create Next Year Copy'}
              </Button>
            </div>
          )}
          <div className="mt-2 text-muted small">
            Showing: <Badge bg="light" text="dark" className="me-1">{formatYearLabel(selectedFyStartYear)}</Badge>
            {selectedUser ? <Badge bg="light" text="dark">{selectedUser}</Badge> : <Badge bg="light" text="dark">All Users</Badge>}
          </div>
        </Card.Body>
      </Card>

      {/* Overall HR Metric */}
      {overallHRPct !== null && (
        <Card className="shadow-sm mb-4 border-0">
          <Card.Body className="py-3">
            <Row className="align-items-center g-2">
              <Col md={6}>
                <div className="text-muted small">Overall HR Completion</div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="h3 mb-0 fw-bold" style={{ color: getPctColor(overallHRPct) }}>
                    {Math.round(overallHRPct)}%
                  </span>
                  {overallMarksPct != null && <span className="text-muted small">({overallMarksPct}%)</span>}
                </div>
              </Col>
              <Col md={6} className="text-md-end">
                <div className="text-muted small">Total Marks</div>
                <div className="h6 mb-0">
                  <span className="text-muted">Total: </span>
                  <span className="fw-bold" style={{ color: getPctColor(overallMarksPct ?? overallHRPct) }}>
                    {Math.round(overallMarks.achieved)} / {Math.round(overallMarks.total)} marks
                  </span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

	      <Row className="g-4 mb-4">
	        <Col lg={12}>
	          <Card className="shadow-sm h-100 border-0">
	            <Card.Header className="bg-white border-bottom">
	              <div className="d-flex justify-content-between align-items-center">
	                <h6 className="mb-0">Module Wise (Achieved vs Total &amp; Percentage of completion)</h6>
	              </div>
	            </Card.Header>
	            <Card.Body style={{ minHeight: 360 }}>
	              <ResponsiveContainer width="100%" height={320}>
	                <BarChart
	                  data={chartData}
	                  margin={{ top: 18, right: 20, left: 0, bottom: 40 }}
	                  barCategoryGap="22%"
	                  barGap={6}
	                >
	                  <CartesianGrid strokeDasharray="3 3" />
		                  <XAxis dataKey="name" tick={false} axisLine={false} tickLine={false} />
		                  <YAxis yAxisId="left" allowDecimals={false} domain={[0, (max) => Math.ceil((Number(max) || 0) * 1.15)]} />
		                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
		                  <Tooltip formatter={(v, k) => {
		                    const labelByKey = {
		                      achieved: 'Achieved Marks',
		                      total: 'Total Marks',
		                      pct: 'Percentage'
		                    };
		                    const value = k === 'pct' ? `${v}%` : v;
		                    return [value, labelByKey[k] || k];
		                  }} />
		                  <Legend verticalAlign="bottom" height={30} />
	                  <Bar yAxisId="left" dataKey="total" fill="#e5e7eb" name="Total" radius={[8, 8, 0, 0]} barSize={28}>
	                    <LabelList dataKey="total" position="top" dy={-6} {...barValueLabelTopProps} />
	                    <LabelList dataKey="name" content={renderVerticalNameLabel} />
	                  </Bar>
	                  <Bar yAxisId="left" dataKey="achieved" fill="#16a34a" name="Achieved" radius={[8, 8, 0, 0]} barSize={28}>
	                    <LabelList dataKey="achieved" position="top" dy={-6} {...barValueLabelTopProps} />
	                  </Bar>
	                  <Bar yAxisId="right" dataKey="pct" fill="#2563eb" name="Percentage" radius={[8, 8, 0, 0]} barSize={28}>
	                    <LabelList dataKey="pct" position="top" dy={-6} formatter={pctLabelFormatter} {...barValueLabelTopProps} />
	                  </Bar>
	                </BarChart>
	              </ResponsiveContainer>
              <div className="text-muted small">
                Percentage shown above each bar = Achieved Marks / Total Marks (weights from each HR sub-dashboard).
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

	      <Card className="shadow-sm mb-4 border-0">
	        <Card.Header className="bg-white border-bottom">
	          <h6 className="mb-0">User-wise Marks (Achieved vs Total &amp; Percentage of completion)</h6>
	        </Card.Header>
	        <Card.Body style={{ minHeight: 360 }}>
	          <ResponsiveContainer width="100%" height={320}>
	            <BarChart
	              data={userWiseChartData}
	              margin={{ top: 28, right: 20, left: 0, bottom: 40 }}
	              barCategoryGap="22%"
	              barGap={6}
	            >
	              <CartesianGrid strokeDasharray="3 3" />
	              <XAxis dataKey="user" tick={false} axisLine={false} tickLine={false} />
	              <YAxis yAxisId="left" allowDecimals={false} domain={[0, (max) => Math.ceil((Number(max) || 0) * 1.15)]} />
	              <YAxis
	                yAxisId="right"
	                orientation="right"
	                domain={[0, 100]}
	                ticks={[0, 25, 50, 75, 100]}
	                tickFormatter={(v) => `${v}%`}
	                padding={{ top: 18 }}
	              />
	              <Tooltip
	                formatter={(v, k) => {
	                  if (k === 'pct') return [`${v}%`, 'Completion'];
	                  if (k === 'achieved') return [v, 'Achieved Marks'];
	                  if (k === 'total') return [v, 'Total Marks'];
	                  return [v, k];
	                }}
	              />
	              <Legend verticalAlign="bottom" height={30} />
	              <Bar yAxisId="left" dataKey="total" fill="#e5e7eb" name="Total" radius={[8, 8, 0, 0]} barSize={28}>
	                <LabelList dataKey="total" position="top" dy={-6} {...barValueLabelTopProps} />
	                <LabelList dataKey="user" content={renderVerticalNameLabel} />
	              </Bar>
	              <Bar yAxisId="left" dataKey="achieved" fill="#16a34a" name="Achieved" radius={[8, 8, 0, 0]} barSize={28}>
	                <LabelList dataKey="achieved" position="top" dy={-6} {...barValueLabelTopProps} />
	              </Bar>
	              <Bar yAxisId="right" dataKey="pct" fill="#2563eb" name="Percentage" radius={[8, 8, 0, 0]} barSize={28}>
	                <LabelList dataKey="pct" position="top" dy={-6} formatter={pctLabelFormatter} {...barValueLabelTopProps} />
	              </Bar>
	            </BarChart>
	          </ResponsiveContainer>
	          <div className="text-muted small">
	            Total marks are split evenly when multiple owners are listed for the same parameter (e.g. "A / B"). Percentage = achieved / total.
	          </div>
	        </Card.Body>
	      </Card>

      {/* Sub-Dashboard Tiles */}
      <Row className="g-4">
        {SUB_DASHBOARDS.map((sub) => {
          const metrics = derivedSubMetrics[sub.id] || { overallPct: null, responsibles: [], totalMarks: 0, achievedMarks: 0, marksPct: null };
          const color = getPctColor(metrics.overallPct);
          return (
            <Col lg={6} key={sub.id}>
              <div className="hr-main-tile card shadow-lg h-100 border-0 position-relative overflow-hidden"
                   onClick={() => handleSubClick(sub)}>
                <div className="card-body p-4 d-flex flex-column h-100">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <div className="h4 mb-1 fw-bold text-dark">{sub.title}</div>
                      <div className="text-muted small">{sub.icon} {sub.description}</div>
                    </div>
                    <FiArrowRight size={24} className="text-primary align-self-start" />
                  </div>

                  <div className="flex-grow-1 d-flex flex-column justify-content-center">
                    <div className="d-flex justify-content-between align-items-end mb-4">
                      <span className="h1 mb-0 fw-bold" style={{color}}>{metrics.overallPct || '--'}%</span>
                      <span className="text-muted small">Overall Completion</span>
                    </div>

	                    {metrics.totalMarks > 0 && (
	                      <div className="mb-3">
	                        <div className="d-flex justify-content-between text-muted small mb-1">
	                          <span>Marks</span>
	                          <span>
	                            {Math.round(metrics.achievedMarks)} / {Math.round(metrics.totalMarks)} ({metrics.marksPct ?? 0}%)
	                          </span>
	                        </div>
	                        <progress
	                          className="w-100"
	                          value={Math.max(0, Math.min(100, metrics.marksPct ?? 0))}
	                          max={100}
	                          style={{ height: 8, accentColor: '#2563eb' }}
	                        />
	                      </div>
	                    )}

                    {metrics.responsibles.length > 0 && (
                      <div className="mb-4">
                        <small className="text-muted mb-1 d-block">Responsible:</small>
                        <div className="responsibles-list">
                          {metrics.responsibles.map((resp, i) => (
                            <span key={i} className="badge bg-light text-dark me-1 mb-1 fw-normal">
                              {resp}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-auto">
                    <div className="d-flex justify-content-between mb-2 small text-muted">
                      <span>Progress</span>
                      <span>{metrics.overallPct || 0}%</span>
                    </div>
                    <div className="progress" style={{height: '8px'}}>
                      <div className="progress-bar progress-bar-fill" 
                           role="progressbar"
                           style={{width: `${metrics.overallPct || 0}%`, backgroundColor: color}}
                            aria-valuenow={metrics.overallPct} 
                            aria-valuemin={0} 
                            aria-valuemax={100}
                          >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </Container>
  );
};

export default HRMainDashboardPage;


